import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createId } from '@/lib/utils/id'

export interface SavedQuery {
  id: string
  label: string
  query: string
  createdAt: number
  lastUsedAt?: number
  useCount: number
}

export interface SavedState {
  items: SavedQuery[]
  save: (input: { label?: string; query: string }) => string
  update: (id: string, patch: Partial<Pick<SavedQuery, 'label' | 'query'>>) => void
  markUsed: (id: string) => void
  remove: (id: string) => void
  clear: () => void
  /** Guards the "already saved" affordance in the chat view. */
  isSaved: (query: string) => boolean
}

function defaultLabel(query: string): string {
  const trimmed = query.trim().replace(/\s+/g, ' ')
  return trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed
}

export const useSavedStore = create<SavedState>()(
  persist(
    (set, get) => ({
      items: [],

      save: ({ label, query }) => {
        const id = createId()
        set((state) => ({
          items: [
            {
              id,
              label: label?.trim() || defaultLabel(query),
              query: query.trim(),
              createdAt: Date.now(),
              useCount: 0,
            },
            ...state.items,
          ],
        }))
        return id
      },

      update: (id, patch) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, ...patch } : item,
          ),
        })),

      markUsed: (id) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? { ...item, lastUsedAt: Date.now(), useCount: item.useCount + 1 }
              : item,
          ),
        })),

      remove: (id) =>
        set((state) => ({ items: state.items.filter((item) => item.id !== id) })),

      clear: () => set({ items: [] }),

      isSaved: (query) => {
        const needle = query.trim().toLowerCase()
        return get().items.some((item) => item.query.trim().toLowerCase() === needle)
      },
    }),
    {
      name: 'qap.saved.v1',
      version: 1,
      partialize: (state) => ({ items: state.items }),
    },
  ),
)
